import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRightIcon, MenuIcon, XIcon } from "lucide-react";
const LOGO_URL = "https://cdn.magicpatterns.com/uploads/ey7t78L7kbQvqY81egZpuN/big-yellow-logo.png";
const CAREERS_URL = "https://bigyellow.au/careers/";
const navigation = [{
  label: "Careers",
  href: "#careers"
}, {
  label: "Community",
  href: "#community"
}, {
  label: "Life at Big Yellow",
  href: "#life"
}, {
  label: "Our work",
  href: "#work"
}, {
  label: "About us",
  href: "https://bigyellow.au/about-us/"
}];
const trackedSections = ["careers", "community", "life", "work"];
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    }, {
      rootMargin: "-45% 0px -50% 0px"
    });
    trackedSections.forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    const hero = document.getElementById("top");
    const clear = new IntersectionObserver(([entry]) => entry.isIntersecting && setActiveSection(""), {
      threshold: 0.5
    });
    if (hero) clear.observe(hero);
    return () => {
      observer.disconnect();
      clear.disconnect();
    };
  }, []);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    const previousActive = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const containFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !mobileMenuRef.current) return;
      const focusable = Array.from(mobileMenuRef.current.querySelectorAll<HTMLElement>('button, [href], [tabindex]:not([tabindex="-1"])'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", containFocus);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", containFocus);
      previousActive?.focus();
    };
  }, [open]);
  const closeMenu = () => setOpen(false);
  return <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${scrolled ? "bg-black/95 shadow-[0_1px_0_rgba(255,255,255,0.1)]" : "bg-transparent"}`} data-id="element-359" data-mp="264pls3ep10wa">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:bg-white focus:px-4 focus:py-3 focus:font-semibold focus:text-black" data-id="element-360" data-mp="1sayb2rp87ayz">
        Skip to main content
      </a>
      <div className="relative mx-auto flex h-20 w-full max-w-[1600px] items-center justify-end gap-3 px-6 lg:h-24 lg:px-12 xl:px-20" data-id="element-361" data-mp="1ie3c67p1j8k6">
        <a href="#top" aria-label="Big Yellow home" className={`absolute left-3 top-2 origin-top-left transition-transform duration-[250ms] ease-[cubic-bezier(0.23,1,0.32,1)] lg:left-9 xl:left-16 ${scrolled ? "scale-[0.62] lg:scale-[0.5] xl:scale-[0.42]" : "scale-100"}`} data-id="element-362" data-mp="1edjije3oen2s">
          <img src={LOGO_URL} alt="Big Yellow" width={188} height={102} className="block h-auto w-[150px] sm:w-[190px] lg:w-[250px] xl:w-[300px]" data-id="element-363" data-mp="3fquy29zrimrn" />
        </a>

        <nav aria-label="Primary navigation" className="hidden items-center gap-8 xl:flex" data-id="element-364" data-mp="36r80y0e2g34e">
          {navigation.map(item => <a key={item.href} href={item.href} aria-current={item.href === `#${activeSection}` ? "location" : undefined} className="nav-link text-white" data-id="element-365" data-mp="3avfp9wgm3h23">
              {item.label}
            </a>)}
          <a href={CAREERS_URL} className="btn btn-primary btn-sm ml-2" data-id="element-366" data-mp="2n40klx1d4nno">
            View opportunities <ArrowUpRightIcon aria-hidden="true" size={17} className="btn-icon" data-id="element-367" data-mp="2n25zoiq3cvjl" />
          </a>
        </nav>

        <a href={CAREERS_URL} className="btn btn-primary btn-sm hidden sm:inline-flex xl:hidden" data-id="element-368" data-mp="2e3c7d4jsmrbu">
          View opportunities <ArrowUpRightIcon aria-hidden="true" size={17} className="btn-icon" data-id="element-369" data-mp="2j2x2f3vuefja" />
        </a>
        <button type="button" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="mobile-navigation" aria-label="Open navigation" className="inline-flex h-12 w-12 items-center justify-center border border-white/50 text-white transition-[background-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-white/15 active:scale-95 xl:hidden" data-id="element-370" data-mp="1zgdinhcbmb0t">
          <MenuIcon aria-hidden="true" size={24} data-id="element-371" data-mp="2idwq4gj4rll4" />
        </button>
      </div>

      {open && <div ref={mobileMenuRef} id="mobile-navigation" className="fixed inset-0 z-[60] overflow-y-auto bg-black px-6 py-5 text-white xl:hidden" role="dialog" aria-modal="true" aria-label="Site navigation" data-id="element-372" data-mp="2dfv87yxfxsb8">
          <div className="flex items-start justify-between" data-id="element-373" data-mp="2n6zn6yn49d62">
            <img src={LOGO_URL} alt="Big Yellow" width={188} height={102} className="h-auto w-[130px]" data-id="element-374" data-mp="3157h0xouvyd0" />
            <button ref={closeButtonRef} type="button" onClick={closeMenu} aria-label="Close navigation" className="inline-flex h-12 w-12 items-center justify-center border border-white/50 transition-[background-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-white/15 active:scale-95" data-id="element-375" data-mp="2w7xhgp7r66bg">
              <XIcon aria-hidden="true" size={25} data-id="element-376" data-mp="22tcybjhu6a8c" />
            </button>
          </div>
          <nav aria-label="Mobile navigation" className="mt-10 flex flex-col border-t border-white/20" data-id="element-377" data-mp="27lwln86bsjqc">
            {navigation.map(item => <a key={item.href} href={item.href} onClick={closeMenu} className="group flex items-center justify-between border-b border-white/20 py-5 text-2xl font-medium transition-colors duration-150 hover:text-[#FFCF53]" data-id="element-378" data-mp="2cmbxtwevfl6q">
                <span className="transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:translate-x-1.5" data-id="element-379" data-mp="2qjh3vwel4k1z">{item.label}</span>
                <ArrowUpRightIcon aria-hidden="true" size={20} className="opacity-50 transition-opacity duration-150 group-hover:opacity-100" data-id="element-380" data-mp="s5xi6a7nlbwf" />
              </a>)}
          </nav>
          <div className="mt-8 grid gap-3" data-id="element-381" data-mp="onrnh05gl370">
            <a href={CAREERS_URL} className="btn btn-primary w-full justify-between" data-id="element-382" data-mp="jvgpi2ua3rh3">
              View opportunities <ArrowUpRightIcon aria-hidden="true" size={20} className="btn-icon" data-id="element-383" data-mp="ew413dnrecce" />
            </a>
            <button type="button" data-open-eoi onClick={closeMenu} className="btn btn-ghost-light w-full justify-between" data-id="element-384" data-mp="av22mz1o24bh">
              Register your interest <ArrowUpRightIcon aria-hidden="true" size={20} className="btn-icon" data-id="element-385" data-mp="17kj3n0ppf26u" />
            </button>
          </div>
          <p className="mt-6 text-sm leading-6 text-white/70" data-id="element-386" data-mp="12jvdsid7ore7">Current vacancies open on Big Yellow&apos;s careers page.</p>
        </div>}
    </header>;
}